import { useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';
import { TbCircleCheck } from 'react-icons/tb';
import { toast } from 'react-toastify';

import { MAX_MOTIVO_SAIDA, MIN_MOTIVO_SAIDA, sairDoPrograma } from 'services/optout.service';

interface Props {
  callback: (action: 'saiu' | 'close') => void;
}

export default function ModalSairPrograma({ callback }: Props) {
  ReactModal.setAppElement('#modal');

  const [motivo, setMotivo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; mensagem?: string } | null>(null);

  const textLength = motivo.trim().length;
  const fullLength = motivo.length;

  const faltam = useMemo(() => Math.max(0, MIN_MOTIVO_SAIDA - textLength), [textLength]);
  const excedeu = fullLength > MAX_MOTIVO_SAIDA;
  const formValid = faltam === 0 && !excedeu;

  /* O interceptor derruba POST/PUT/DELETE em modo somente leitura com um Error
     cru. Barrar aqui troca isso por uma mensagem que a pessoa entende. */
  const somenteLeitura = !!localStorage.getItem('readOnly');

  async function handleSubmit() {
    if (!formValid || sending) return;

    if (somenteLeitura) {
      toast.error('Não é possível registrar a saída no modo somente leitura.', { position: 'top-right' });
      return;
    }

    setSending(true);

    try {
      const resposta = await sairDoPrograma(motivo);
      setResult(resposta);
    } catch (e) {
      toast.error('Não foi possível registrar sua saída. Tente novamente em alguns minutos.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setSending(false);
    }
  }

  function contador() {
    if (excedeu) {
      return `Limite de ${MAX_MOTIVO_SAIDA} caracteres ultrapassado`;
    }
    if (faltam > 0) {
      return `${faltam > 1 ? 'Faltam' : 'Falta'} ${faltam} caractere${faltam > 1 ? 's' : ''} para o mínimo`;
    }
    return `${fullLength} de ${MAX_MOTIVO_SAIDA} caracteres`;
  }

  return (
    <ReactModal
      shouldCloseOnOverlayClick={false}
      isOpen={true}
      contentLabel="Não quero mais participar"
      portalClassName="relative z-50"
      overlayClassName="rb-modal-overlay rb-modal-overlay--top"
      contentElement={() => (
        <div className="rb-modal rb-modal--md">
          <button
            className="rb-modal__close"
            aria-label="Fechar janela"
            onClick={() => !sending && callback(result?.ok ? 'saiu' : 'close')}
            disabled={sending}
          >
            <IoIosClose size={26} />
          </button>

          {result ? (
            <>
              <div className="rb-modal__header">
                {result.ok && (
                  <div className="flex items-center justify-center">
                    <TbCircleCheck size={72} color="var(--color-primary)" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                )}
                <h1 className="rb-modal__title">
                  {result.ok ? 'Sua saída foi registrada' : 'Não foi possível registrar sua saída'}
                </h1>
              </div>

              <p className="rb-modal__text" style={{ textAlign: 'center' }}>
                {result.mensagem ||
                  (result.ok
                    ? 'Você não aparece mais para receber reconhecimentos.'
                    : 'Revise o motivo e tente novamente.')}
              </p>

              {result.ok && (
                <p className="rb-modal__text" style={{ textAlign: 'center' }}>
                  Vamos encerrar sua sessão agora.
                </p>
              )}

              <div className="rb-modal__actions">
                <button className="rb-btn-primary" onClick={() => callback(result.ok ? 'saiu' : 'close')}>
                  {result.ok ? 'Encerrar sessão' : 'Fechar'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="rb-modal__header">
                <h1 className="rb-modal__title">Não quero mais participar</h1>
              </div>

              <p className="rb-modal__text">
                Ao sair, você deixa de aparecer na busca e não pode mais enviar nem receber reconhecimentos. Os
                reconhecimentos que você já trocou continuam no mural — eles foram escritos por colegas e não são
                apagados. Seus selos ficam guardados como estão.
              </p>

              <p className="rb-modal__text">
                Para voltar a participar depois, será preciso procurar a área responsável pelo Reconheça.
              </p>

              <div className="rb-modal__field">
                <label className="rb-modal__label" htmlFor="motivo-saida">
                  Por que você prefere não continuar no Reconheça?
                </label>
                <textarea
                  id="motivo-saida"
                  className="rb-input"
                  rows={4}
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  placeholder="Conte brevemente o seu motivo."
                />
                <p
                  className="rb-modal__text"
                  style={{ margin: '6px 0 0', fontSize: 13, color: excedeu ? 'var(--color-danger)' : undefined }}
                  aria-live="polite"
                >
                  {contador()}
                </p>
              </div>

              <div className="rb-modal__actions">
                <button className="rb-btn-primary" onClick={handleSubmit} disabled={!formValid || sending}>
                  {sending ? 'Registrando...' : 'Confirmar saída'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    />
  );
}
